/* ═══════════════════════════════════════════════════════════════
   SavedViewsMenu — full CRUD saved views for Forecasting's Plan mode
   (surface 'forecasting.plan'). Rebuilt on the kit: AnchoredPopover
   (reused from DataTable, same shell DataTable's own saved-views menu
   uses), kit Button/Input, ConfirmDialog for delete (no native confirm).
   Drift indicator: "(modified)" when the live config differs from the
   saved active view.
   ═══════════════════════════════════════════════════════════════ */

import React, { useRef, useState } from 'react';
import { Star, Share2, Pencil, Trash2, ChevronDown, Check } from 'lucide-react';
import type { DashboardView } from '../../../shared/entities';
import { AnchoredPopover } from '../../components/DataTable';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { Icon } from '../../components/ui/Icon';
import { rem } from '../../lib/rem';

export function SavedViewsMenu({
  savedViews,
  activeViewId,
  currentUserId,
  viewIsDirty,
  onApplyView,
  onSaveAs,
  onUpdateActive,
  onDeleteView,
  onSetDefaultView,
  onRenameView,
  onShareToggle,
  canShareViews = true,
}: {
  savedViews: DashboardView[];
  activeViewId: string | null;
  currentUserId: string;
  viewIsDirty: boolean;
  onApplyView: (v: DashboardView) => void;
  onSaveAs: () => void;
  onUpdateActive: () => Promise<void>;
  onDeleteView: (id: string) => Promise<void>;
  onSetDefaultView: (id: string) => Promise<void>;
  onRenameView: (id: string, name: string) => Promise<void>;
  onShareToggle: (id: string) => Promise<void>;
  /** dashboards.share_views — hides the share/unshare toggle for users who
   *  can't publish a view to the firm. Defaults true so existing callers
   *  that don't pass it keep today's behavior. */
  canShareViews?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<DashboardView | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const active = savedViews.find((v) => v.id === activeViewId);
  const ownViews = savedViews.filter((v) => v.userId === currentUserId);
  const sharedViews = savedViews.filter((v) => v.userId !== currentUserId && v.shared);

  function startRename(v: DashboardView) {
    setRenamingId(v.id);
    setRenameValue(v.name);
  }
  async function commitRename() {
    if (renamingId && renameValue.trim()) {
      await onRenameView(renamingId, renameValue.trim());
    }
    setRenamingId(null);
  }

  function renderRow(v: DashboardView, mine: boolean) {
    const isActive = v.id === activeViewId;
    if (renamingId === v.id) {
      return (
        <div
          key={v.id}
          style={{ padding: `${rem(6)} ${rem(10)}`, borderBottom: '1px solid var(--bd)' }}
        >
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename();
              else if (e.key === 'Escape') setRenamingId(null);
            }}
            onBlur={commitRename}
            autoFocus
            style={{ fontSize: 'calc(var(--font-size-base) * 12 / 13)', height: rem(28) }}
          />
        </div>
      );
    }
    return (
      <div
        key={v.id}
        style={{
          padding: `${rem(6)} ${rem(10)}`,
          fontSize: 'calc(var(--font-size-base) * 12 / 13)',
          background: isActive ? 'var(--sa)' : undefined,
          color: 'var(--tx)',
          fontWeight: isActive ? 600 : 500,
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-1)',
        }}
      >
        <button
          onClick={() => {
            onApplyView(v);
            setOpen(false);
          }}
          style={{
            flex: 1,
            textAlign: 'left',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'inherit',
            font: 'inherit',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            padding: `var(--space-1) ${rem(2)}`,
            minHeight: rem(28),
          }}
          title={v.name}
        >
          {v.isDefault && <Icon icon={Star} em={0.9167} fill="var(--ac)" color="var(--ac)" />}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.name}</span>
          {v.shared && (
            <span style={{ fontSize: 'calc(var(--font-size-base) * 9 / 13)', color: 'var(--tm)' }}>
              · shared
            </span>
          )}
        </button>
        {mine && (
          <>
            <IconBtn
              title={v.isDefault ? 'Default view' : 'Set as default'}
              onClick={() => onSetDefaultView(v.id)}
              active={!!v.isDefault}
            >
              <Icon icon={Star} size="md" fill={v.isDefault ? 'currentColor' : 'none'} />
            </IconBtn>
            {(canShareViews || v.shared) && (
              <IconBtn
                title={v.shared ? 'Shared with firm — click to unshare' : 'Share with firm'}
                onClick={() => onShareToggle(v.id)}
                active={!!v.shared}
              >
                <Icon icon={Share2} size="md" />
              </IconBtn>
            )}
            <IconBtn title="Rename" onClick={() => startRename(v)}>
              <Icon icon={Pencil} size="md" />
            </IconBtn>
            <IconBtn title="Delete" onClick={() => setConfirmDelete(v)} danger>
              <Icon icon={Trash2} size="md" />
            </IconBtn>
          </>
        )}
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <Button ref={btnRef} variant="outline" size="sm" onClick={() => setOpen((v) => !v)}>
        {active ? `View: ${active.name}` : 'Views'}
        {active && viewIsDirty && (
          <span style={{ color: 'var(--ac)', fontWeight: 700 }} title="Unsaved changes">
            ●
          </span>
        )}
        <Icon icon={ChevronDown} em={1.2} />
      </Button>
      {open && btnRef.current && (
        <AnchoredPopover
          anchorRect={btnRef.current.getBoundingClientRect()}
          width={300}
          onClose={() => setOpen(false)}
          ignoreRef={btnRef}
        >
          <div style={{ maxHeight: rem(360), overflowY: 'auto' }}>
            {ownViews.length === 0 && sharedViews.length === 0 && (
              <div
                style={{
                  padding: 'var(--space-3)',
                  fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                  color: 'var(--tm)',
                }}
              >
                No saved views yet.
              </div>
            )}
            {ownViews.length > 0 && (
              <>
                <SectionHead>My views</SectionHead>
                {ownViews.map((v) => renderRow(v, true))}
              </>
            )}
            {sharedViews.length > 0 && (
              <>
                <SectionHead>Shared</SectionHead>
                {sharedViews.map((v) => renderRow(v, false))}
              </>
            )}
            <div style={{ borderTop: '1px solid var(--bd)' }}>
              {active && viewIsDirty && (
                <MenuAction
                  onClick={async () => {
                    await onUpdateActive();
                    setOpen(false);
                  }}
                  icon={<Icon icon={Check} size="md" />}
                >
                  Save changes to "{active.name}"
                </MenuAction>
              )}
              <MenuAction
                onClick={() => {
                  onSaveAs();
                  setOpen(false);
                }}
              >
                + Save as new view…
              </MenuAction>
            </div>
          </div>
        </AnchoredPopover>
      )}
      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title="Delete saved view?"
        description={`"${confirmDelete?.name}" will be permanently deleted. This can't be undone.`}
        confirmLabel="Delete"
        destructive
        onConfirm={async () => {
          if (confirmDelete) await onDeleteView(confirmDelete.id);
          setConfirmDelete(null);
        }}
      />
    </div>
  );
}

function SectionHead({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: `${rem(6)} var(--space-3) ${rem(2)}`,
        fontSize: 'calc(var(--font-size-base) * 9 / 13)',
        fontWeight: 700,
        color: 'var(--tm)',
        textTransform: 'uppercase',
        letterSpacing: '.06em',
        borderTop: '1px solid var(--bd)',
      }}
    >
      {children}
    </div>
  );
}

function IconBtn({
  children,
  onClick,
  title,
  active,
  danger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  active?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      title={title}
      aria-label={title}
      style={{
        width: rem(26),
        height: rem(26),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        border: 'none',
        borderRadius: 'var(--radius-sm)',
        cursor: 'pointer',
        color: danger ? 'var(--color-danger)' : active ? 'var(--ac)' : 'var(--tm)',
        flexShrink: 0,
      }}
    >
      {children}
    </button>
  );
}

function MenuAction({
  children,
  onClick,
  icon,
}: {
  children: React.ReactNode;
  onClick: () => void;
  icon?: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      role="menuitem"
      style={{
        width: '100%',
        textAlign: 'left',
        padding: 'var(--space-2) var(--space-3)',
        fontSize: 'calc(var(--font-size-base) * 12 / 13)',
        cursor: 'pointer',
        color: 'var(--ac)',
        fontWeight: 600,
        background: 'none',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: rem(6),
      }}
    >
      {icon}
      {children}
    </button>
  );
}
